import React, { useContext, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";

import Modal from "@stadium/ui/src/components/modal";
import Loaders from "@stadium/ui/src/components/loaders/loaders";

import { useQuery } from "@stadium/services/apollo-client";
import { useStoreContext } from "@components/store/provider";
import { useCreateSwagContext } from "@contexts/create-swag-context";

import { getCountry } from "@selectors/country";
import { getStoreCurrencyCode } from "@selectors/store";

import {
  fetchProductDetail,
  fetchProductDetailWithNumber,
} from "@graphql/queries/stores/products/fetch-detail";

import ProductDetailIndex from "@components/store/product-detail";
import { useSendItemsContext } from "@contexts/send-items-context";

import ProductDetailProvider from "@contexts/product-detail-context";

import { InternalButton } from "@sharedComponents/buttons";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import BottomIconHideWrapper from "@stadium/shared/src/components/bottom-icon-hide-wrapper";

import styles from "./main.module.scss";

const { Content, Header, Body, Footer } = Modal;

const ProductDetailsModal = ({ closeModal, data }) => {
  const country = useSelector(getCountry);
  const storeCurrencyCode = useSelector(getStoreCurrencyCode);
  const { storeNumber } = useStoreContext();
  const { draftSwagData, isSwagCreationShopFlow } = useCreateSwagContext();

  const { isDesktopDevice } = useContext(CurrentDeviceContext);

  const { volumeDiscountProductsVariables } = useSendItemsContext();

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeModal?.();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, []);

  const productDetailQuery = isSwagCreationShopFlow
    ? fetchProductDetailWithNumber
    : fetchProductDetail;

  const {
    productData = {},
    viewingInsideByoMedley = false,
    showAddToBox = false,
    showFooter = true,
    footerPrimaryTextCta = "",
    hideVariantSelection = false,
    hidePrice = false,
    customVariantSelector = () => {},
    footerPrimaryCtaHandler = () => {},
    viewOnlyMode = false,
    renderCatalogModeFooterLinks = () => {},
    hideBrandLink = false,
    selectedProductImg,
    selectedVariantSliderImages,
    classes = {},
    canBuyParentProduct,
  } = data;

  const productInfo = {
    showAddToBox,
    hidePriceDetail: hidePrice,
    hideVariantSelectionSection: hideVariantSelection,
    renderCustomVariantSelectionSection: customVariantSelector,
    isViewOnlyMode: viewOnlyMode,
    renderCatalogModeFooterLinks,
    onSuccessCallback: closeModal,
    modalClasses: classes,
    selectedProductImg,
    selectedVariantSliderImages,
    hideBrandLink,
    canBuyParentProduct,
  };

  const variables = {
    slug: productData.slug,
    currency: storeCurrencyCode,
    ...(!isSwagCreationShopFlow && { country: country.iso }),
    storeNumber: productData.storeNumber || storeNumber,
    viewingInsideByoMedley,
    ...(isSwagCreationShopFlow && { number: draftSwagData?.number }),
    ...volumeDiscountProductsVariables,
  };

  const footerDoneHandler = () => {
    footerPrimaryCtaHandler();
    closeModal();
  };

  const { data: productDetailData, loading } = useQuery(productDetailQuery, {
    variables,
    fetchPolicy: "cache-and-network",
  });

  const product = useMemo(() => productDetailData?.product, [productDetailData]);

  if (loading || !productDetailData?.product)
    return <Loaders.PageLoader wrapperClass="pageLoaderwrap min-h-100" />;

  return (
    <BottomIconHideWrapper hideIcon={!isDesktopDevice}>
      <Content
        className={`${styles.primaryModalContent} ${styles.productDetailsModal} ${
          viewOnlyMode ? styles.productCatalogModal : ""
        }
        ${isSwagCreationShopFlow ? styles.swagProductModal : ""}
        
        `}
      >
        <Header className={styles.modalHeader}>
          {/* using button for accessibility */}
          <InternalButton
            onClick={closeModal}
            autoFocus
            className={`bs-close-modal ${styles.closeModal} ${styles.productDetailsClose}`}
            aria-label="Close"
            styleType="no-style"
          />
        </Header>
        <Body className={`${styles.modalBody}`}>
          <ProductDetailProvider showAddToBox={showAddToBox}>
            <ProductDetailIndex product={product} data={productInfo} />
          </ProductDetailProvider>
        </Body>
        {showFooter && (
          <Footer className={styles.modalFooter}>
            <InternalButton styleType="link" onClick={closeModal}>
              close
            </InternalButton>

            <InternalButton styleType="primary" onClick={footerDoneHandler}>
              {footerPrimaryTextCta}
            </InternalButton>
          </Footer>
        )}
      </Content>
    </BottomIconHideWrapper>
  );
};

export default ProductDetailsModal;
